import type { GridPaginationModel } from "@mui/x-data-grid/models/gridPaginationProps";
import type { GridSortModel } from "@mui/x-data-grid/models/gridSortModel";
import { useModulePagedListQuery } from "entities/modules/queries/useModuleTemplatePagedListQuery";
import { ModuleData } from "entities/modules/types";
import { FC, PropsWithChildren, useState } from "react";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { useLocalSearch } from "shared/hooks/useLocalSearch";
import useToggle from "shared/hooks/useToggle";
import { TableSortModel } from "shared/types/tables";
import { createContext } from "shared/utils/context";
import { ModulesPageType } from "./types";

const [Provider, useModulesPage] = createContext<ModulesPageType>({
  name: "ModulesPageContext",
  hookName: "useModulesPage",
  providerName: "ModulesPageProvider",
});

const ModulesPageProvider: FC<PropsWithChildren> = (props) => {
  const moduleDetailsDrawer = useDrawerState<ModuleData>();

  const [openDeleteModal, setOpenDeleteModal] = useToggle(false);
  const [selectedModuleTemplate, setSelectedModuleTemplate] =
    useState<ModuleData | null>(null);
  const [sortModel, setSortModel] = useState<TableSortModel>(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState({
    currentPage: 0,
    prevPage: 0,
  });

  const { data, isFetching, fetchNextPage, fetchPreviousPage } =
    useModulePagedListQuery(rowsPerPage, page, sortModel);

  const currentPageItems = data?.pages[page.currentPage]?.items.items ?? [];
  const totalItemsCount = data?.pages[0].items.totalCount ?? 0;
  const gridPaginationModel: GridPaginationModel = {
    page: page.currentPage,
    pageSize: rowsPerPage,
  };

  const searchData = useLocalSearch({
    searchableFields: ["name"],
    initialData: currentPageItems || [],
  });

  const handleSortModelChange = (model: GridSortModel) => {
    const firstSorting = model[0];

    if (firstSorting?.sort) {
      setSortModel({
        sortBy: firstSorting.field,
        sortDir: firstSorting.sort,
      });
    } else {
      setSortModel(null);
    }

    setPage({
      currentPage: 0,
      prevPage: 0,
    });
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const shouldFetchNextPage = page.currentPage < model.page;
    const shouldFetchPrevPage = page.currentPage > model.page;
    const rowsPerPageChanged = rowsPerPage !== model.pageSize;

    if (shouldFetchNextPage) {
      fetchNextPage();
    } else if (shouldFetchPrevPage) {
      fetchPreviousPage();
    }

    setRowsPerPage(model.pageSize);

    if (rowsPerPageChanged) {
      setPage({
        currentPage: 0,
        prevPage: 0,
      });
    } else {
      setPage((prevPagination) => ({
        currentPage: model.page,
        prevPage: prevPagination.currentPage,
      }));
    }
  };

  const handleDeleteModal = () => {
    setOpenDeleteModal();

    if (openDeleteModal) {
      setSelectedModuleTemplate(null);
    }
  };

  const value: ModulesPageType = {
    isLoading: isFetching,
    currentPageItems,
    totalItemsCount,
    gridPaginationModel,
    handleSortModelChange,
    handlePaginationModelChange,
    moduleDetailsDrawer,
    selectedModuleTemplate,
    setSelectedModuleTemplate,
    openDeleteModal,
    handleDeleteModal,
    searchData: {
      value: searchData.value,
      onChange: searchData.onChange,
      filteredData: searchData.filteredData,
    },
  };

  return <Provider value={value}>{props.children}</Provider>;
};

export { ModulesPageProvider, useModulesPage };
